import {
  PlusIcon,
  Squares2X2Icon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';

import { ChequeDetails } from '@/components/postgrid/cheque-details';
import { LetterDetails } from '@/components/postgrid/postgrid-letters/letter-details/LetterDetails';
import { MailableList } from '@/components/postgrid/postgrid-mailables/mailable-list/MailableList';
import { MailableTable } from '@/components/postgrid/postgrid-mailables/mailable-table/MailableTable';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Heading } from '@/components/ui/catalyst/heading';
import { Text } from '@/components/ui/catalyst/text';

type ViewMode = 'table' | 'list';
type CreateDialogType = 'cheque' | 'letter' | null;

/**
 * Correspondence Page - Letters & cheques aggregated view with list/table toggle
 */
export const CorrespondencePage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [selectedMatterId, setSelectedMatterId] = useState<string>('');
  const [selectedMailableId, setSelectedMailableId] = useState<string | null>(
    null,
  );
  const [selectedMailableType, setSelectedMailableType] = useState<
    'cheque' | 'letter' | null
  >(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [createDialogType, setCreateDialogType] =
    useState<CreateDialogType>(null);

  const handleDetailsClick = (id: string, type: 'cheque' | 'letter') => {
    setSelectedMailableId(id);
    setSelectedMailableType(type);
    setIsDetailsOpen(true);
  };

  const handleCreateSuccess = () => {
    setCreateDialogType(null);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedMailableId(null);
    setSelectedMailableType(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Heading>Correspondence</Heading>
          <Text>Manage letters and cheques</Text>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggles */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <Button
              color={viewMode === 'table' ? 'blue' : 'zinc'}
              onClick={() => setViewMode('table')}
              className="px-3 py-1 text-sm"
            >
              <ViewColumnsIcon className="w-4 h-4" />
              Table
            </Button>
            <Button
              color={viewMode === 'list' ? 'blue' : 'zinc'}
              onClick={() => setViewMode('list')}
              className="px-3 py-1 text-sm"
            >
              <Squares2X2Icon className="w-4 h-4" />
              List
            </Button>
          </div>

          {/* Create Actions */}
          <div className="flex gap-2">
            <Button onClick={() => setCreateDialogType('letter')} color="blue">
              <PlusIcon className="w-4 h-4" />
              Letter
            </Button>
            <Button onClick={() => setCreateDialogType('cheque')} color="green">
              <PlusIcon className="w-4 h-4" />
              Cheque
            </Button>
          </div>
        </div>
      </div>

      {/* Matter Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4">
          <Text className="font-medium">Filter by Matter:</Text>
          <div className="flex-1 max-w-md">
            <select
              value={selectedMatterId}
              onChange={(e) => setSelectedMatterId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Matters</option>
              {/* TODO: Add matter options from a matters query */}
            </select>
          </div>
          {selectedMatterId && (
            <Button color="zinc" onClick={() => setSelectedMatterId('')}>
              Clear Filter
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow">
        {viewMode === 'table' ? (
          <MailableTable
            matterId={selectedMatterId || undefined}
            onDetailsClick={handleDetailsClick}
          />
        ) : (
          <div className="p-6">
            <MailableList
              matterId={selectedMatterId || undefined}
              onDetailsClick={handleDetailsClick}
            />
          </div>
        )}
      </div>

      {/* Details Drawers */}
      {selectedMailableType === 'cheque' && selectedMailableId && (
        <ChequeDetails
          chequeId={selectedMailableId}
          open={isDetailsOpen}
          onOpenChange={handleCloseDetails}
        />
      )}

      {selectedMailableType === 'letter' && selectedMailableId && (
        <LetterDetails
          letterId={selectedMailableId}
          open={isDetailsOpen}
          onOpenChange={handleCloseDetails}
        />
      )}

      {/* Create Dialogs - Now redirect to wizard pages */}
      <Dialog
        open={createDialogType === 'cheque'}
        onClose={() => setCreateDialogType(null)}
      >
        <DialogTitle>Create New Cheque</DialogTitle>
        <DialogBody>
          {selectedMatterId ? (
            <div className="space-y-4">
              <Text>Redirecting to cheque wizard...</Text>
              <Button
                href={`/matters/${selectedMatterId}/mail/cheque-wizard`}
                color="blue"
                onClick={() => setCreateDialogType(null)}
              >
                Continue to Cheque Wizard
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <Text>Please select a matter to create a cheque for:</Text>
              <select
                value={selectedMatterId}
                onChange={(e) => setSelectedMatterId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a matter...</option>
                {/* TODO: Add matter options from a matters query */}
              </select>
            </div>
          )}
        </DialogBody>
      </Dialog>

      <Dialog
        open={createDialogType === 'letter'}
        onClose={() => setCreateDialogType(null)}
      >
        <DialogTitle>Create New Letter</DialogTitle>
        <DialogBody>
          {selectedMatterId ? (
            <div className="space-y-4">
              <Text>Redirecting to letter wizard...</Text>
              <Button
                href={`/matters/${selectedMatterId}/mail/letter-wizard`}
                color="blue"
                onClick={() => setCreateDialogType(null)}
              >
                Continue to Letter Wizard
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <Text>Please select a matter to create a letter for:</Text>
              <select
                value={selectedMatterId}
                onChange={(e) => setSelectedMatterId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a matter...</option>
                {/* TODO: Add matter options from a matters query */}
              </select>
            </div>
          )}
        </DialogBody>
      </Dialog>
    </div>
  );
};
